export type DevicePlatform =
  | 'ios_safari'
  | 'ios_chrome'
  | 'ios_brave'
  | 'android_chrome'
  | 'android_brave'
  | 'android_firefox'
  | 'desktop'
  | 'unknown';

export function detectPlatform(): DevicePlatform {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua) && !/CriOS/i.test(ua);
  const isChrome = /Chrome/i.test(ua) || /CriOS/i.test(ua);
  const isBrave = (navigator as any).brave !== undefined;
  const isFirefox = /Firefox/i.test(ua);

  if (isIOS) {
    if (isBrave) return 'ios_brave';
    if (isChrome) return 'ios_chrome';
    if (isSafari) return 'ios_safari';
  }
  if (isAndroid) {
    if (isBrave) return 'android_brave';
    if (isFirefox) return 'android_firefox';
    if (isChrome) return 'android_chrome';
  }
  return 'desktop';
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

export function shouldShowInstallPrompt(): boolean {
  if (isStandalone()) return false;
  const platform = detectPlatform();
  if (platform === 'desktop') return false;
  if (platform === 'unknown') return false;

  const dismissed = localStorage.getItem('papacito_install_dismissed');
  if (dismissed) {
    const hoursSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60);
    if (hoursSince < 24) return false;
  }
  return true;
}

const SESSION_COUNT_KEY = 'papacito_session_count';
const INSTALL_DISMISSED_TRIGGERS = 'papacito_dismissed_triggers';

export function trackSession(): number {
  const count = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0');
  localStorage.setItem(SESSION_COUNT_KEY, String(count + 1));
  return count + 1;
}

export function getSessionCount(): number {
  return parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0');
}

export function dismissInstallTrigger(trigger: string) {
  const dismissed: string[] = JSON.parse(localStorage.getItem(INSTALL_DISMISSED_TRIGGERS) || '[]');
  if (!dismissed.includes(trigger)) {
    dismissed.push(trigger);
    localStorage.setItem(INSTALL_DISMISSED_TRIGGERS, JSON.stringify(dismissed));
  }
}

export function isTriggerDismissed(trigger: string): boolean {
  const dismissed: string[] = JSON.parse(localStorage.getItem(INSTALL_DISMISSED_TRIGGERS) || '[]');
  return dismissed.includes(trigger);
}

export function shouldShowInstallNudge(): boolean {
  if (isStandalone()) return false;
  const platform = detectPlatform();
  if (platform === 'desktop') return false;
  if (platform === 'unknown') return false;
  return true;
}

export function recordInstallTrigger(triggerId: string) {
  localStorage.setItem('papacito_last_install_trigger', triggerId);
}

export type InstallStep = {
  number: number;
  title: string;
  description: string;
  highlightArea: 'bottom_center' | 'bottom_right' | 'top_right' | 'middle_screen';
  icon: string;
};

export const INSTALL_STEPS: Record<string, InstallStep[]> = {
  ios_safari: [
    {
      number: 1,
      title: 'Tap the Share button',
      description: 'At the bottom center of Safari',
      highlightArea: 'bottom_center',
      icon: '\u2B06\uFE0F',
    },
    {
      number: 2,
      title: 'Tap "Add to Home Screen"',
      description: 'Scroll down in the menu to find it',
      highlightArea: 'middle_screen',
      icon: '\uD83D\uDCF1',
    },
    {
      number: 3,
      title: 'Tap "Add"',
      description: 'Top right corner to confirm',
      highlightArea: 'top_right',
      icon: '\u2705',
    },
  ],
  ios_brave: [
    {
      number: 1,
      title: 'Tap the \u22EF menu',
      description: 'Bottom right of your Brave browser',
      highlightArea: 'bottom_right',
      icon: '\u22EF',
    },
    {
      number: 2,
      title: 'Tap "Share"',
      description: 'In the menu that opens',
      highlightArea: 'middle_screen',
      icon: '\u2B06\uFE0F',
    },
    {
      number: 3,
      title: 'Tap "Add to Home Screen"',
      description: 'Scroll down to find it',
      highlightArea: 'middle_screen',
      icon: '\uD83D\uDCF1',
    },
  ],
  ios_chrome: [
    {
      number: 1,
      title: 'Tap the \u22EF menu',
      description: 'Bottom right of your browser',
      highlightArea: 'bottom_right',
      icon: '\u22EF',
    },
    {
      number: 2,
      title: 'Tap "Add to Home Screen"',
      description: 'In the menu that opens',
      highlightArea: 'middle_screen',
      icon: '\uD83D\uDCF1',
    },
    {
      number: 3,
      title: 'Tap "Add"',
      description: 'To confirm the installation',
      highlightArea: 'top_right',
      icon: '\u2705',
    },
  ],
  android_chrome: [
    {
      number: 1,
      title: 'Tap the \u22EE menu',
      description: 'Top right of Chrome browser',
      highlightArea: 'top_right',
      icon: '\u22EE',
    },
    {
      number: 2,
      title: 'Tap "Add to Home screen"',
      description: 'In the dropdown menu',
      highlightArea: 'middle_screen',
      icon: '\uD83D\uDCF1',
    },
    {
      number: 3,
      title: 'Tap "Add"',
      description: 'To install the app',
      highlightArea: 'bottom_center',
      icon: '\u2705',
    },
  ],
  android_brave: [
    {
      number: 1,
      title: 'Tap the \u22EE menu',
      description: 'Bottom right of Brave browser',
      highlightArea: 'bottom_right',
      icon: '\u22EE',
    },
    {
      number: 2,
      title: 'Tap "Add to Home Screen"',
      description: 'In the menu that opens',
      highlightArea: 'middle_screen',
      icon: '\uD83D\uDCF1',
    },
    {
      number: 3,
      title: 'Tap "Install"',
      description: 'To confirm',
      highlightArea: 'bottom_center',
      icon: '\u2705',
    },
  ],
  android_firefox: [
    {
      number: 1,
      title: 'Tap the \u22EE menu',
      description: 'Top right of Firefox',
      highlightArea: 'top_right',
      icon: '\u22EE',
    },
    {
      number: 2,
      title: 'Tap "Install"',
      description: 'To add to home screen',
      highlightArea: 'middle_screen',
      icon: '\u2705',
    },
  ],
};
